// frontend/src/components/postproduction/FinancialTable.tsx
import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
  Chip,
  Button
} from '@mui/material';
import { PerformanceIntelligence } from '../../types/PPDash';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

interface FinancialTableProps {
  performances: PerformanceIntelligence[];
}

type SortField = 'date' | 'name' | 'revenue' | 'costs' | 'subsidy' | 'capacity' | 'attendance';
type SortOrder = 'asc' | 'desc';

const FinancialTable: React.FC<FinancialTableProps> = ({ performances }) => {
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle order if clicking same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New field - default to desc for numbers, asc for text
      setSortField(field);
      setSortOrder(field === 'name' ? 'asc' : 'desc');
    }
  };

  const handleReset = () => {
    setSortField('date');
    setSortOrder('desc');
  };

  const sortedPerformances = [...performances].sort((a, b) => {
    let aVal: any;
    let bVal: any;

    switch (sortField) {
      case 'date':
        aVal = new Date(a.date).getTime();
        bVal = new Date(b.date).getTime();
        break;
      case 'name':
        aVal = a.name.toLowerCase();
        bVal = b.name.toLowerCase();
        break;
      case 'revenue':
        aVal = a.financial.revenue;
        bVal = b.financial.revenue;
        break;
      case 'costs':
        aVal = a.financial.costs;
        bVal = b.financial.costs;
        break;
      case 'subsidy':
        aVal = a.financial.subsidy;
        bVal = b.financial.subsidy;
        break;
      case 'capacity':
        aVal = a.attendance.capacity || 0;
        bVal = b.attendance.capacity || 0;
        break;
      case 'attendance':
        aVal = a.attendance.totalAttendance;
        bVal = b.attendance.totalAttendance;
        break;
      default:
        return 0;
    }

    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const formatCurrency = (amount: number) => {
    return `€${amount.toLocaleString('en-IE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const getSubsidyColor = (subsidy: number) => {
    if (subsidy < 0) return 'success'; // Profit
    if (subsidy === 0) return 'default'; // Break even
    if (subsidy < 100) return 'warning'; // Small subsidy
    return 'error'; // Large subsidy
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Financial Performance by Show
        </Typography>
        <Button
          startIcon={<RestartAltIcon />}
          onClick={handleReset}
          variant="outlined"
          size="small"
        >
          Reset Sort
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ backgroundColor: 'background.paper' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'date'}
                  direction={sortField === 'date' ? sortOrder : 'desc'}
                  onClick={() => handleSort('date')}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'name'}
                  direction={sortField === 'name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  Artist
                </TableSortLabel>
              </TableCell>
              <TableCell>Venue</TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'attendance'}
                  direction={sortField === 'attendance' ? sortOrder : 'desc'}
                  onClick={() => handleSort('attendance')}
                >
                  Attendance
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'capacity'}
                  direction={sortField === 'capacity' ? sortOrder : 'desc'}
                  onClick={() => handleSort('capacity')}
                >
                  Capacity %
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'revenue'}
                  direction={sortField === 'revenue' ? sortOrder : 'desc'}
                  onClick={() => handleSort('revenue')}
                >
                  Revenue
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'costs'}
                  direction={sortField === 'costs' ? sortOrder : 'desc'}
                  onClick={() => handleSort('costs')}
                >
                  Costs
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'subsidy'}
                  direction={sortField === 'subsidy' ? sortOrder : 'desc'}
                  onClick={() => handleSort('subsidy')}
                >
                  Subsidy
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">€/Ticket</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedPerformances.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No performances found for the selected criteria
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedPerformances.map((performance) => (
                <TableRow 
                  key={performance._id}
                  hover
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell>{formatDate(performance.date)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {performance.name}
                    </Typography>
                    {performance.genre && (
                      <Typography variant="caption" color="text.secondary">
                        {performance.genre}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {performance.venue.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Cap: {performance.venue.capacity}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {performance.attendance.totalAttendance}
                  </TableCell>
                  <TableCell align="right">
                    {performance.attendance.capacity !== null 
                      ? `${performance.attendance.capacity}%` 
                      : 'N/A'}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(performance.financial.revenue)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(performance.financial.costs)}
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={formatCurrency(performance.financial.subsidy)}
                      color={getSubsidyColor(performance.financial.subsidy)}
                      size="small"
                      sx={{ minWidth: 80 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(performance.financial.subsidyPerTicket)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {sortedPerformances.length > 0 && (
        <Box sx={{ mt: 2, textAlign: 'right' }}>
          <Typography variant="caption" color="text.secondary">
            Showing {sortedPerformances.length} performance{sortedPerformances.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default FinancialTable;